/**
 * F3 (paridad Ledgeria) — Tesorería: cuentas bancarias, extractos y
 * conciliación.
 *
 * Importes SIEMPRE en céntimos con signo desde el punto de vista del titular:
 * positivo entra dinero, negativo sale. Es la convención contraria a la del
 * fichero del banco (que habla de su propio debe/haber), y se traduce en el
 * parser para que aquí no haya que pensarlo.
 */
import { z } from 'zod';
export const BankTransactionStatusSchema = z.enum(['PENDIENTE', 'CONCILIADO', 'IGNORADO']);
/** IBAN sin espacios ni guiones, en mayúsculas. */
export const IbanSchema = z
    .string()
    .trim()
    .transform((s) => s.replace(/[\s-]/g, '').toUpperCase())
    .refine((s) => /^[A-Z]{2}\d{2}[A-Z0-9]{10,30}$/.test(s), { message: 'IBAN no válido' });
export const BankAccountDtoSchema = z.object({
    id: z.string(),
    name: z.string(),
    iban: z.string(),
    accountCode: z.string(),
    currency: z.string(),
    active: z.boolean(),
    lastBalanceCents: z.number().int().nullable(),
    lastBalanceAt: z.string().nullable(),
    /**
     * Saldo AL DÍA deducido del feed: el que dijo el banco más los movimientos
     * llegados después. `null` cuando no se puede afirmar (ver
     * `lib/banking/current-balance.ts`), y entonces se enseña solo el del banco.
     * `.default(null)` para que un cliente viejo no reviente contra un backend nuevo.
     */
    computedBalanceCents: z.number().int().nullable().default(null),
    /** Movimientos posteriores a la lectura del saldo: cuántos y por cuánto. */
    balanceDeltaCents: z.number().int().default(0),
    balanceDeltaCount: z.number().int().min(0).default(0),
    /** Movimientos sin conciliar: es el número que importa del día a día. */
    pendingCount: z.number().int().min(0),
    /** Origen de los movimientos: MANUAL (solo N43) o ENABLE_BANKING (PSD2). */
    feed: z.string().default('MANUAL'),
    /** Fin del consentimiento PSD2; hay que renovarlo antes de esa fecha. */
    feedValidUntil: z.string().nullable().default(null),
    feedLastSyncAt: z.string().nullable().default(null),
    feedLastError: z.string().nullable().default(null),
});
export const BankAccountListResponseSchema = z.object({
    items: z.array(BankAccountDtoSchema),
});
export const CreateBankAccountSchema = z.object({
    name: z.string().trim().min(1).max(120),
    iban: IbanSchema,
    /** Subcuenta de tesorería (572…). */
    accountCode: z.string().trim().regex(/^\d{3,8}$/, 'Cuenta contable no válida'),
    currency: z.string().trim().length(3).default('EUR'),
});
export const UpdateBankAccountSchema = CreateBankAccountSchema.partial().extend({
    active: z.boolean().optional(),
});
export const BankTransactionDtoSchema = z.object({
    id: z.string(),
    bankAccountId: z.string(),
    operationDate: z.string(),
    valueDate: z.string(),
    amountCents: z.number().int(),
    balanceCents: z.number().int().nullable(),
    concept: z.string(),
    reference: z.string().nullable(),
    documentNumber: z.string().nullable(),
    status: BankTransactionStatusSchema,
    /**
     * Céntimos del movimiento ya aplicados (sin signo). Lo que falta por aplicar
     * es `Math.abs(amountCents) - appliedCents`; mientras sea mayor que cero el
     * apunte sigue pendiente aunque ya haya pagado alguna factura.
     * `.default(0)` para que un cliente viejo no reviente contra un backend nuevo.
     */
    appliedCents: z.number().int().default(0),
    /**
     * Todo lo que ha pagado este movimiento, en orden. Un apunte puede cubrir
     * varias facturas; `matchedInvoiceId` solo guarda la primera y se conserva por
     * compatibilidad. `kind: 'writeoff'` es el sobrante dado por cerrado.
     */
    applications: z
        .array(z.object({
        kind: z.string(),
        targetId: z.string().nullable(),
        label: z.string().nullable(),
        amountCents: z.number().int(),
    }))
        .default([]),
    matchedInvoiceId: z.string().nullable(),
    matchedExpenseId: z.string().nullable(),
    /** Referencia legible de lo conciliado ("F-2026-0007", "2026/45"). */
    matchedLabel: z.string().nullable(),
    reconciledAt: z.string().nullable(),
    notes: z.string().nullable(),
});
export const BankTransactionListResponseSchema = z.object({
    items: z.array(BankTransactionDtoSchema),
    page: z.number().int().min(1),
    pageSize: z.number().int().min(1),
    total: z.number().int().min(0),
    /** Totales del filtro actual, para no tener que sumarlos a ojo. */
    totals: z.object({
        inCents: z.number().int(),
        outCents: z.number().int(),
        pendingCount: z.number().int().min(0),
    }),
});
export const BankStatementDtoSchema = z.object({
    id: z.string(),
    fileName: z.string(),
    format: z.string(),
    fromDate: z.string(),
    toDate: z.string(),
    openingBalanceCents: z.number().int(),
    closingBalanceCents: z.number().int(),
    transactionCount: z.number().int().min(0),
    importedCount: z.number().int().min(0),
    duplicateCount: z.number().int().min(0),
    importedAt: z.string(),
    importedByName: z.string().nullable(),
});
/** Resultado de importar un fichero de extracto. */
export const ImportStatementResponseSchema = z.object({
    statement: BankStatementDtoSchema,
    /** Avisos del parser que no invalidan el fichero. */
    warnings: z.array(z.string()),
    /**
     * Contraste del saldo final del banco con el saldo CONTABLE de la subcuenta.
     * Si no coinciden, faltan asientos por conciliar (que es lo normal justo
     * después de importar) o hay un movimiento sin registrar.
     */
    balanceCheck: z.object({
        bankCents: z.number().int(),
        ledgerCents: z.number().int(),
        diffCents: z.number().int(),
    }),
});
export const MatchCandidateSchema = z.object({
    kind: z.enum(['invoice', 'expense']),
    id: z.string(),
    number: z.string(),
    partyName: z.string(),
    partyNif: z.string().nullable(),
    date: z.string(),
    pendingCents: z.number().int(),
    totalCents: z.number().int(),
    score: z.number(),
    reasons: z.array(z.string()),
    exactAmount: z.boolean(),
});
export const MatchCandidatesResponseSchema = z.object({
    items: z.array(MatchCandidateSchema),
});
/** Confirmación de conciliación: siempre la decide una persona. */
export const ReconcileTransactionSchema = z.object({
    kind: z.enum(['invoice', 'expense']),
    targetId: z.string().min(1),
    /**
     * Importe a aplicar, en céntimos positivos. Si se omite se aplica el importe
     * del movimiento, que es el caso normal; se admite menos para movimientos
     * que agrupan varias facturas.
     */
    amountCents: z.number().int().positive().optional(),
    notes: z.string().max(500).optional(),
});
/**
 * Reembolso a quien adelantó dinero de su bolsillo: el movimiento de salida del
 * banco se aplica contra su cuenta corriente de socio (551) en vez de contra
 * una factura o un gasto. Una sola transferencia puede saldar varios gastos a
 * la vez, que es como se hace en la práctica.
 */
export const SettlePartnerSchema = z.object({
    userId: z.string().min(1),
    /** Céntimos positivos. Si se omite, se aplica todo lo que queda del apunte. */
    amountCents: z.number().int().positive().optional(),
    /**
     * QUÉ gastos cubre esta transferencia, línea de reparto a línea de reparto.
     * Se elige a mano (decisión de Jesús, 21/08/2026) en vez de repartir por
     * antigüedad: así el reembolso dice exactamente lo que se ha devuelto.
     *
     * La suma tiene que cuadrar con el importe liquidado. Si se omite, el asiento
     * 551→572 se hace igual pero no se marca ningún gasto como reembolsado — útil
     * para saldar a mano una cuenta descuadrada, no para el uso normal.
     */
    allocations: z
        .array(z.object({
        expensePayerId: z.string().min(1),
        amountCents: z.number().int().positive(),
    }))
        .max(200)
        .optional(),
});
export const PartnerBalanceSchema = z.object({
    userId: z.string(),
    name: z.string(),
    accountCode: z.string(),
    /** Saldo acreedor: positivo = la empresa le debe ese dinero. */
    owedCents: z.number().int(),
});
export const PartnerBalanceListResponseSchema = z.object({
    items: z.array(PartnerBalanceSchema),
});
/** Un adelanto pendiente de devolver, para elegir qué cubre una transferencia. */
export const PartnerAdvanceSchema = z.object({
    expensePayerId: z.string(),
    expenseId: z.string(),
    code: z.string(),
    supplierName: z.string(),
    concept: z.string(),
    paidAt: z.string(),
    amountCents: z.number().int(),
    reimbursedCents: z.number().int(),
    pendingCents: z.number().int(),
});
export const PartnerAdvancesResponseSchema = z.object({
    items: z.array(PartnerAdvanceSchema),
    totalPendingCents: z.number().int(),
});
//# sourceMappingURL=banking.js.map